import type { ActivityLocation, DayRow } from '$/dto/ItineraryData.dto.ts';
import type { TripData } from '$/models/Trip.model.ts';
import BookingPartner, { type BookingPartner as Partner, type BookingPartnerKey } from '@/_constants/BookingPartner.ts';
import type { BookableStay } from '@/utils/booking/isBookable.ts';
import { partyOf, stayDates } from '@/utils/booking/bookingTrip.ts';
import partnerLink, { type LinkTarget } from '@/utils/booking/partnerLink.ts';

export type BookingItem = { kind: 'hotel'; stay: BookableStay } | { kind: 'activity'; row: DayRow };

export type BookingLink = { partner: BookingPartnerKey; name: string; url: string };

export type BookingTripContext = {
  tripId: string;
  arrivalDate: string | null | undefined;
  data: TripData | null | undefined;
};

const bookingPlaceName = (row: Pick<DayRow, 'activity_title' | 'location'>): string => {
  const name = row.location.name.trim();
  const title = row.activity_title.trim();

  return title && !title.toLowerCase().includes(name.toLowerCase()) ? title : name;
};

export const bookingSearchText = (location: Pick<ActivityLocation, 'name' | 'city'>): string =>
  [location.name, location.city]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');

const targetFor = (item: BookingItem, trip: BookingTripContext, today: string): LinkTarget =>
  item.kind === 'hotel'
    ? {
        kind: 'hotel',
        place: item.stay.place,
        city: item.stay.city ?? null,
        agodaHotelId: item.stay.agoda_hotel_id ?? null,
        agodaCityId: item.stay.agoda_city_id ?? null,
        dates: stayDates(trip.arrivalDate, item.stay.check_in_day, item.stay.check_out_day, today),
        party: partyOf(trip.data),
      }
    : { kind: 'activity', searchText: bookingSearchText(item.row.location), name: bookingPlaceName(item.row) };

const bookingLinksFor = (
  item: BookingItem,
  trip: BookingTripContext,
  today: string,
  partners: readonly Partner[] = BookingPartner
): BookingLink[] => {
  const target = targetFor(item, trip, today);

  return partners
    .filter((partner) => partner.enabled && partner.covers === item.kind)
    .flatMap((partner) => {
      const url = partnerLink(partner, target, trip.tripId);
      return url ? [{ partner: partner.key, name: partner.name, url }] : [];
    });
};

export default bookingLinksFor;
